import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { Equipment } from 'src/app/shared/models/dtos/register-form/equipment/equipment';
import { EquipmentDTO } from 'src/app/shared/models/dtos/responses/equipment-dto/equipment-dto';
import { AddRemoveEquipmentRequestDTO } from 'src/app/shared/models/dtos/requests/add-remove-equipments-request-dto/add-remove-equipments-request-dto';
import { SerialModelValidationResponseDTO } from 'src/app/shared/models/dtos/responses/serial-model-validation-response-dto/serial-model-validation-response-dto';
import { EquipmentSAPRegistrationRequestDTO } from 'src/app/shared/models/dtos/requests/equipment-sap-registrarion-request-dto/equipment-sap-registration-request-dto';
import { SAPProductResponseDTO } from 'src/app/shared/models/dtos/responses/sap-product-response-dto/sap-product-reponse-dto';

@Injectable({ providedIn: 'root' })
/**
 * Service to interact with LnR's Product Service
 */
export class ProductService {
  private backendURL = '/prd/api';
  private backendAuthURL = '/prd/api/a';

  /**
   * Injects HttpClient to interact with LnR Backend
   * @constructor
   */
  constructor(private httpClient: HttpClient) {}

  /**
   * Get a printer by passing serial number
   * @param {string} serial represents a printer serial code
   * @return {Promise<string>}
   */
  getPrinter(serial: string): Observable<any> {
    const endpoint = `equipment/model/${serial}`;
    return this.httpClient
      .get(this.buildURL(endpoint))
      .pipe(map((result) => result));
  }

  /**
   * @return {Observable<Equipment[]>}
   */
  getBusinessEquipments(): Observable<Equipment[]> {
    const endpoint = 'equipment/list-from-business';
    return this.httpClient.get<Equipment[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Gets the equipments.
   * @param {number} page
   * @param {number} pageSize
   * @return {Observable<Equipment[]>}
   */
  getBusinessEquipmentsPaginated(
    page: number,
    pageSize: number
  ): Observable<Equipment[]> {
    const endpoint = 'equipment/list-from-business/paged';
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());
    return this.httpClient.get<Equipment[]>(this.buildAuthURL(endpoint), {
      params,
    });
  }

  /**
   * @param {number} modelName
   * @return {Observable<EquipmentDTO[]>}
   */
  getBusinessEquipmentsModel(modelName: String): Observable<EquipmentDTO[]> {
    const endpoint = `equipment/list-from-business/model/${modelName}`;
    return this.httpClient.get<EquipmentDTO[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Returns a list of equipmnets associated to user's business
   * @return {Observable<EquipmentDTO[]>}
   */
  listEquipmentsFromBusinessUser(): Observable<EquipmentDTO[]> {
    const endpoint = 'equipment/business';
    return this.httpClient.get<EquipmentDTO[]>(this.buildAuthURL(endpoint));
  }

  /**
   * Adds or deletes a list of equipments
   * @param {AddRemoveEquipmentRequestDTO} list
   * @return {any}
   */
  updateEquipmentList(list: AddRemoveEquipmentRequestDTO): Observable<any> {
    const endpoint = 'equipment';

    return this.httpClient.post<any>(this.buildAuthURL(endpoint), list);
  }

  /**
   * Gets model name by modelId
   * @param {number} modelId
   * @return {any}
   */
  getPrinterModelById(modelId: number) {
    const endpoint = `model/${modelId}`;
    return this.httpClient.get<any>(this.buildAuthURL(endpoint));
  }

  /**
   * Receives an array of serials and the country initials
   * @param {string[]} serials
   * @param {string} countryInitials
   * @return {Observable<SerialModelValidationResponseDTO[]>}
   */
  validateSerial(
    serials: string[],
    countryInitials: string
  ): Observable<SerialModelValidationResponseDTO[]> {
    const endpoint = 'equipment/serial/validate';
    return this.httpClient.post<SerialModelValidationResponseDTO[]>(
      this.buildURL(endpoint),
      {
        serialNumbers: serials,
        country: countryInitials,
      }
    );
  }

  /**
   * Get equipment family by serial
   * @param {string} serial
   * @return {Observable<any>}
   */
  getEquipmentFamilyBySerial(serial: string): Observable<any> {
    const endpoint = `equipment/family/serial/${serial}`;
    return this.httpClient.get<any>(this.buildURL(endpoint));
  }

  /**
   * Registers an equipment on Epson's SAP
   * @param {EquipmentSAPRegistrationRequestDTO} equipment
   * @return {Observable<SAPProductResponseDTO>}
   */
  epsonSAPEquipmentRegistration(
    equipment: EquipmentSAPRegistrationRequestDTO
  ): Observable<SAPProductResponseDTO> {
    const endpoint = 'equipment/sap-registration';
    return this.httpClient.post<SAPProductResponseDTO>(
      this.buildURL(endpoint),
      equipment
    );
  }

  /**
   * Builds URL from endpoint
   * @param {string} endpoint
   * @return {string}
   */
  private buildURL(endpoint: string): string {
    return `${this.backendURL}/${endpoint}`;
  }

  /**
   * Builds URL from endpoint with authentication
   * @param {string} endpoint
   * @return {string}
   */
  private buildAuthURL(endpoint: string): string {
    return `${this.backendAuthURL}/${endpoint}`;
  }

  /**
   * Gets a product service API version
   * @return {Observable<string>}
   */
  getApiVersion(): Observable<string> {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    return this.httpClient.get(`${this.backendURL}/version`, {
      headers,
      responseType: 'text',
    });
  }
}
